import * as React from 'react';
import { useState, useEffect } from 'react';
import { MyModelsFolder, MyModelsPageState } from '../../pages/subpages/MyModels';
import apiService from '../../services/ApiService';
import { useDrag, useDrop } from 'react-dnd';
import { getEmptyImage } from 'react-dnd-html5-backend';
import ModelListFolderOptionMenu from './ModelListFolderOptionMenu';
import TailwindModal from '../Tailwind/TailwindModal';
import TailwindButton from '../Tailwind/TailwindButton';
import { cn } from '../../lib/utils';

const deleteModal = (folder: MyModelsFolder, state: MyModelsPageState, reloadState: Function) => {
    const parentElem = state.folders[folder.folderId.toString()]

    const handleClose = () => {
        folder.deleteModalOpen = false;
        reloadState();
    }

    const handleDelete = () => {
        apiService.deleteFolder(folder.id).then(response => {
            if (response) {
                folder.deleteModalOpen = false;
                const index = parentElem.folderChildrensIds.indexOf(folder.id);
                if (index !== -1) {
                    parentElem.folderChildrensIds.splice(index, 1);
                }
                delete state.folders[folder.id.toString()]
                reloadState();
            }
        })
    }

    return (
        <TailwindModal
            isOpen={folder.deleteModalOpen}
            onClose={handleClose}
            size="sm"
            showCloseButton={false}
        >
            <div className="flex flex-col items-center gap-4">
                <h2 className="text-xl font-bold text-center text-slate-900">
                    Are you sure?
                </h2>
                <p className="text-sm text-center text-slate-700">
                    This will delete <b>{folder.name}</b> and all its content permanently.
                </p>
                <div className="flex justify-center gap-3 mt-2">
                    <TailwindButton variant="ghost" icon="close" size="sm" onClick={handleClose}>
                        Cancel
                    </TailwindButton>
                    <TailwindButton variant="danger" icon="delete_forever" size="sm" onClick={handleDelete}>
                        Yes, Delete
                    </TailwindButton>
                </div>
            </div>
        </TailwindModal>
    );
}

function RenameForm({ folder, reloadState }: { folder: MyModelsFolder, reloadState: Function }) {
    const [name, setName] = useState(folder.name);
    const [error, setError] = useState(false);

    const onSubmit = (event) => {
        event.preventDefault();
        if (!name.trim()) {
            setError(true);
            return;
        }
        if (folder.id) {
            apiService.putFolder(folder.id, name, folder.folderId).then(response => {
                if (response) {
                    folder.name = name;
                    folder.editing = false;
                    reloadState();
                }
            });
        }
    };

    return (
        <div className="px-3 pb-2">
            <form onSubmit={onSubmit}>
                <div className="relative flex items-center gap-1">
                    <input
                        type="text"
                        value={name}
                        className={cn(
                            "flex-1 min-w-0 border-b bg-transparent text-sm text-slate-900 focus:outline-none focus:border-primary transition-colors",
                            error ? "border-red-400" : "border-slate-300"
                        )}
                        onChange={(e) => { setName(e.target.value); if (error) setError(false); }}
                        onFocus={event => { event.target.select(); }}
                        autoFocus
                    />
                    <button
                        type="submit"
                        className="size-7 rounded-lg text-slate-500 hover:bg-slate-100 transition-colors flex items-center justify-center"
                        aria-label="Save"
                    >
                        <span className="material-symbols-outlined text-[18px]">done</span>
                    </button>
                    <button
                        type="button"
                        className="size-7 rounded-lg text-slate-500 hover:bg-slate-100 transition-colors flex items-center justify-center"
                        onClick={() => { folder.editing = false; reloadState(); }}
                        aria-label="Cancel"
                    >
                        <span className="material-symbols-outlined text-[18px]">close</span>
                    </button>
                    {error && (
                        <p className="absolute bottom-full left-0 mb-1 text-xs text-red-500 bg-white border border-red-100 rounded px-1.5 py-0.5 shadow-sm whitespace-nowrap">
                            Name cannot be empty
                        </p>
                    )}
                </div>
            </form>
        </div>
    );
}

export default function ModelGridFolder({ folder, state, reloadState, setCurrentFolder, moveFolder, moveModel }) {
        const [menuAnchorEl, setMenuAnchorEl] = useState<HTMLElement | null>(null);
        const [{ isDragging }, drag, preview] = useDrag(() => ({
            type: 'ELEM',
            collect: (monitor) => ({
              isDragging: !!monitor.isDragging(),
            }),
            item: { ...folder, type: 'FOLDER' },
          }),
          [folder]
        )

        useEffect(() => {
            preview(getEmptyImage(), { captureDraggingState: true });
        }, [preview]);

        const [{ isOver }, drop] = useDrop(
            () => ({
              accept: 'ELEM',
              drop: (item: any, _monitor) => {
                if (item.type == 'FOLDER') {
                    moveFolder(item, folder);
                } else if (item.type == 'MODEL') {
                    moveModel(item, folder);
                }
              },
              collect: (monitor) => ({
                isOver: !!monitor.isOver()
              })
            }),
            [moveFolder, moveModel]
        )

        return (
            <div ref={drop}>
                <div ref={(folder.editing) ? null : drag} style={{ cursor: 'move', opacity: isDragging ? 0.5 : 1 }}>
                    <div className="w-full h-[180px]">
                        <button
                            className="block text-left w-full h-full border-0 bg-transparent"
                            onClick={(e) => {
                                // on doubleclick
                                if (e.detail == 2 && !folder.editing) {
                                    setCurrentFolder(folder)
                                }
                            }}
                        >
                            <div
                                className={cn(
                                    "models-item-component card-hover flex flex-col w-full h-full border border-slate-100 bg-white rounded-xl transition-all group",
                                    isOver && "ring-2 ring-primary border-primary"
                                )}
                            >
                                <div className="flex items-center px-2 pt-2">
                                    <div className="flex-grow" />
                                    <button
                                        className="size-7 rounded-lg text-slate-400 hover:bg-slate-100 hover:text-slate-600 transition-colors flex items-center justify-center"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setMenuAnchorEl(e.currentTarget);
                                        }}
                                        aria-label="Options"
                                    >
                                        <span className="material-symbols-outlined text-[18px]">more_vert</span>
                                    </button>
                                </div>
                                <div className="flex-1 flex items-center justify-center">
                                    <div className="size-14 rounded-xl bg-slate-50 text-slate-400 flex items-center justify-center group-hover:bg-primary group-hover:text-white transition-colors shadow-sm">
                                        <span className="material-symbols-outlined text-[32px]">folder</span>
                                    </div>
                                </div>
                                {
                                    folder.editing ?
                                    <RenameForm folder={folder} reloadState={reloadState} /> :
                                    <p className="px-3 pb-3 text-sm font-bold text-slate-900 truncate group-hover:text-primary transition-colors">{folder.name}</p>
                                }
                                <ModelListFolderOptionMenu
                                    folder={folder}
                                    reloadState={reloadState}
                                    setCurrentFolder={setCurrentFolder}
                                    anchorOrigin={{
                                        vertical: 'bottom',
                                        horizontal: 'right',
                                    }}
                                    transformOrigin={{
                                        vertical: 'top',
                                        horizontal: 'left',
                                    }}
                                    anchorEl={menuAnchorEl}
                                    onClose={() => setMenuAnchorEl(null)}
                                />
                            </div>
                        </button>
                    </div>
                    {deleteModal(folder, state, reloadState)}
                </div>
            </div>
        )
}
